import React, { useState, useRef, useEffect } from "react";
import CssTransition from "../../../component/layout/css-transition";
import useDebounce from "../../../Hook/useDebounce";

const option = [
  {
    id: 0,
    status: "all",
  },
  {
    id: 1,
    status: "completed",
  },
  {
    id: 2,
    status: "pending",
  },

  {
    id: 3,
    status: "in progress",
  },
];

function StatusFilter({ setStatus, status, options = option, defaultSelection=[2] }) {

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCheckbox, setSelectedCheckbox] = useState(defaultSelection);

  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Use the debounce hook to get the debounced version of the handleOnChange function
  const debouncedHandleCheck = useDebounce(selectedCheckbox, 1000);

  useEffect(() => {
    setStatus(debouncedHandleCheck.map((item) => {
        const statusElement = options.find((element) => element.id === item);
        return statusElement.status
    }))
  }, [debouncedHandleCheck]);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  // handle onChange event
  const handleOnChange = (checkboxId) => {
    const existingSelectedCheckbox = [...selectedCheckbox];
    if (existingSelectedCheckbox.some((element) => element === checkboxId)) {
      // User cannot deselect if only checkbox is checked
      if (existingSelectedCheckbox.length === 1) {
        return null;
      }
      existingSelectedCheckbox.splice(
        existingSelectedCheckbox.findIndex((element) => element === checkboxId),
        1
      );
      setSelectedCheckbox(existingSelectedCheckbox);
    } else {
      // if all status get selected then remove every status expect "All"
      if (checkboxId === 0) {
        setSelectedCheckbox([checkboxId]);
      } else if (
        checkboxId !== 0 &&
        existingSelectedCheckbox.some((element) => element === 0)
      ) {
        // if User selected any other status and "All" status is already selected then "All" will be remove
        existingSelectedCheckbox.splice(
          existingSelectedCheckbox.findIndex((element) => element === 0),
          1,
          checkboxId
        );
        setSelectedCheckbox(existingSelectedCheckbox);
      } else {
        existingSelectedCheckbox.push(checkboxId);
        setSelectedCheckbox(existingSelectedCheckbox);
      }
    }
  };

  return (
    <div className={"relative inline-flex min-w-[210px] w-52"}>
      <button
        id="order-overview-status-filter"
        type="button"
        ref={trigger}
        aria-label="Select date range"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
        className="btn justify-between w-full bg-white border-slate-200 hover:border-slate-300 text-slate-500 hover:text-slate-600"
      >
        <span className="flex items-center">
          <span className="capitalize">
            {options[selectedCheckbox[0]].status}
            {selectedCheckbox.length > 1 && (
              <span>&nbsp;+{selectedCheckbox.length - 1}</span>
            )}
          </span>
        </span>
        <svg
          className="shrink-0 ml-1 fill-current text-slate-400"
          width="11"
          height="7"
          viewBox="0 0 11 7"
        >
          <path d="M5.4 6.8L0 1.4 1.4 0l4 4 4-4 1.4 1.4z" />
        </svg>
      </button>
      <CssTransition
        show={dropdownOpen}
        tag="div"
        className="z-10 absolute top-full left-0 w-full bg-white border border-slate-200 py-1.5 rounded shadow-lg overflow-hidden mt-1"
        enter="transition ease-out duration-100 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          className="font-medium text-sm text-slate-600"
          onFocus={() => setDropdownOpen(true)}
        >
          <div className="p-2 space-y-3">
            {options.map((option) => {
              return (
                <div className="flex items-center space-x-2">
                  <input
                    id={option.status}
                    type="checkbox"
                    className="form-checkbox"
                    checked={selectedCheckbox.some(
                      (element) => element === option.id
                    )}
                    onChange={(e) => handleOnChange(option.id)}
                  />
                  <label
                    htmlFor={option.status}
                    className="text-[13px] text-[#535860] tracking-tight leading-3 font-medium cursor-pointer capitalize"
                  >
                    {option.status}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      </CssTransition>
    </div>
  );
}

export default StatusFilter;
